import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@getro/rombo';
import { debounce } from 'debounce';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { CustomFieldBadge } from '../../../../../atoms/CustomFieldBadge';

const FilterLabel = styled.div`
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 400;
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
`;

const LabelText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CustomFieldText = ({ value, setFilters, field }) => {
  const filterKey = `customFields_${field.id}`;
  const initialText = Array.isArray(value) && value.length > 0 ? value[0] : '';
  const [currentValue, setCurrentValue] = useState(initialText);
  const setFiltersRef = useRef(setFilters);

  useEffect(() => {
    setFiltersRef.current = setFilters;
  }, [setFilters]);

  useEffect(() => {
    const newText = Array.isArray(value) && value.length > 0 ? value[0] : '';
    setCurrentValue((prev) => (prev !== newText ? newText : prev));
  }, [value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSubmit = useCallback(
    debounce((val) => {
      setFiltersRef.current({
        [filterKey]: val.trim().length > 0 ? [val.trim()] : [],
      });
    }, 500),
    [filterKey],
  );

  useEffect(
    () => () => {
      debouncedSubmit.clear?.();
    },
    [debouncedSubmit],
  );

  useEffect(() => {
    const handleResetFilters = () => {
      debouncedSubmit.clear?.();
      setCurrentValue('');
    };
    document.addEventListener('resetFilters', handleResetFilters);

    return () => {
      document.removeEventListener('resetFilters', handleResetFilters);
    };
  }, [debouncedSubmit]);

  const onChange = (event) => {
    setCurrentValue(event.target.value);
    debouncedSubmit(event.target.value);
  };

  const scopeLabel = field.sharedList?.id ? 'List-specific' : 'Global';

  return (
    <div>
      <FilterLabel>
        <LabelWrapper>
          <LabelText title={field.name}>{field.name}</LabelText>
          <CustomFieldBadge>{scopeLabel}</CustomFieldBadge>
        </LabelWrapper>
      </FilterLabel>
      <Input size="small" placeholder="Type to filter..." value={currentValue} onChange={onChange} />
    </div>
  );
};

CustomFieldText.propTypes = {
  value: PropTypes.array,
  setFilters: PropTypes.func.isRequired,
  field: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    sharedList: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

CustomFieldText.defaultProps = {
  value: [],
};
